import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { COACH_SEES_ENGINE } from "../lib/coachEngine";
import { Workspace } from "../components/Shell";
import { ClipPlayButton } from "../components/ClipPlayer";
import { SetNote } from "../components/SetNote";
import { clipsMuted, setClipsMuted, subscribeClipsMuted } from "../lib/clipsMuted";
import { MoveParts } from "../components/MoveParts";
import { calendarToday } from "../lib/days";
import { href, routeQuery } from "../lib/route";
import { Avatar } from "../components/Avatar";
import { supabase } from "../lib/supabase";
import { arrowTarget, clipsByRow, COACH_SEES_RPE, DONE_CLASS, MOVED_NONE, movedParts, positionLine, queuePosition, skipTarget, type SheetExercise, type SheetSetRow } from "../lib/reviewSheet";
import { reviewTitle, splitTitled, threadOf } from "../lib/reviewBody";
import { reviewState, seenWords } from "../lib/reviewState";
import { applySetFieldWrites } from "../lib/setFieldWrite";
import { announceReviewSent } from "../lib/reviewEvents";
import { currentPending, lastDone, pendingWords, startPending, subscribePending, undoPending } from "../lib/pendingSend";
import { updateSets } from "../lib/updateSets";
import { rowChangeParts } from "../lib/proposalOverlay";
import { describeOutcome } from "../lib/assign";
import { tellTheClient } from "../lib/assignPush";
import { shortDate } from "../lib/weekLine";
import {
  cellsForRows, cellTextFor, cellUnitFor, cellWrites, commentsToSend, draftedTree, messageRows, MODE_LINK, nextWeekAbsent, nextWeekCells, nextWeekWords,
  canRepeat, EDIT_NEXT_WEEK_LABEL, primaryLabel, REPEAT_LABEL, repeatDay, SENT_LINE, sendBlocker, showsNextWeek, slotOfRow, UNDO_SECONDS, type CommentDraft, type NextCell,
} from "../lib/reviewWrite";
import { useProposals, useRosterData } from "./useRosterData";
import { VideoThumb } from "../components/VideoThumb";
import { ReviewRecordingBubble, reviewRemove, reviewUpload, SentMedia, useReviewRecorder, type ReviewRecorder, type SentTakes } from "../components/ReviewMedia";
import { hasTake, mediaFields, mediaHome, OVERALL, replyBody, replySlot, uploadItems, VIDEO_UNSUPPORTED, type MediaKind, type SlotKey } from "../lib/reviewMedia";
import { forgetUploads, runUploads, undoUploads } from "../lib/mediaUpload";
import { COMPOSER_MAX, COMPOSER_OVERALL_MAX, ReviewComposer } from "../components/ReviewComposer";
import { useReviewQueue } from "./useReviewQueue";
import { useReviewSheet, type ExerciseMedia } from "./useReviewSheet";
import { useReviewWrite } from "./useReviewWrite";
import { useReviewMode } from "./useReviewMode";
import { WorkoutBuilder } from "./WorkoutBuilder";
import { Icon } from "../components/Icon";

/* THE WORKOUT REVIEW — `#/review/<sessionId>` (W82 §2, the pro web step 3: READ-ONLY). A PAGE, not a window: it is the
 * coach's main work surface, reached from the Dashboard's queue (`‹ Review queue · 2 of 6`, `Skip`, ← / →).
 *
 * ⚠️ THIS FILE DERIVES NOTHING. The queue (its order, the chosen record, finished, written, the flags) is
 * `useReviewQueue` → `reviewQueue.ts`, the SAME hook the Dashboard and the sidebar's count read; the sheet (the pairing,
 * the five states, the words) is `useReviewSheet` → `reviewSheet.ts`. The mock `w81/mock/review.html` is the drawing.
 *
 * THE WRITE (step 4): the right column is "Your review" (the overall words) and "Next week"; under every exercise card
 * `+ Comment` opens ONE inline comment (his bound: one per exercise), which becomes `Edit comment`. Everything is a DRAFT
 * on this screen until the ONE primary — `Send review · set next week` (with_review, a next week exists) or `Send review`
 * — and the send waits `UNDO_SECONDS` with `Undo` before it inserts: a sent row is final (the grant is `read_at` alone),
 * so the page says `Sent · add to it in the thread` afterwards. The rows are `reviewWrite.messageRows`; next week's cells
 * are written through `setFieldWrite` (the overlay's Hold path) and the trees are then READ AGAIN.
 *
 * THE MEDIA (step 5, `components/ReviewMedia.tsx`; job 3 step 5 page 2): every exercise, the overall and a reply are ONE
 * composer each (`components/ReviewComposer.tsx` — `+` · the growing field · the mic inside, the phone's names); the page
 * holds ONE recorder (`useReviewRecorder`). Takes stay in this tab until the
 * send COMMITS — after the undo window — when ONE ordered queue uploads them (`runUploads`) and only then are the rows
 * inserted; an insert that fails removes the uploaded objects again (`undoUploads`). */
/** No coach exercise says km (before the sheet has read the library). */
const NO_KM: ReadonlySet<string> = new Set();
const NO_MEDIA: ReadonlyMap<string, ExerciseMedia> = new Map();

/** `nextWeekWorkoutId`: the builder open over this page on next week's copy (`#/review/<s>/next-week/<copy>`), else null. */
export function WorkoutReview({ sessionId, coachId, nextWeekWorkoutId }: { sessionId: string; coachId: string; nextWeekWorkoutId: string | null }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [sessionId]);
  const [today] = useState(() => calendarToday());
  const roster = useRosterData(0, today);
  const data = roster.status === "ready" ? roster.data : null;
  const proposals = useProposals(data);
  const queue = useReviewQueue(data, proposals, today);
  const q = queue.status === "ready" ? queue.queue : null;
  const row = useMemo(() => q?.days.find((r) => r.sessionId === sessionId) ?? null, [q, sessionId]);
  const position = q === null ? null : queuePosition(q.waiting, sessionId);
  const state = useReviewSheet(row);
  const sheet = state.status === "ready" ? state.sheet : null;
  const kmKeys = state.status === "ready" ? state.kmKeys : NO_KM;
  const media = state.status === "ready" ? state.media : NO_MEDIA;
  const failed = roster.status === "error" ? roster.message : queue.status === "error" ? queue.message : state.status === "error" ? state.message : null;
  const loading = failed === null && (q === null || (row !== null && state.status === "loading"));
  const outside = failed === null && q !== null && (row === null || (state.status === "ready" && sheet === null));

  // ← and → walk the queue (never while a field has the keyboard, never with a modifier — those are the browser's).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t !== null && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));
      const next = arrowTarget(position, e.key, typing, e.altKey || e.metaKey || e.ctrlKey || e.shiftKey);
      if (next === null) return;
      e.preventDefault();
      window.location.hash = href({ name: "review", sessionId: next });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [position]);

  const skip = skipTarget(position);
  const skipHref = skip === null ? href({ name: "dashboard" }) : href({ name: "review", sessionId: skip });

  /* ── the write ── */
  const write = useReviewWrite(row);
  const w = write.state.status === "ready" ? write.state : null;
  const modeState = useReviewMode(coachId);
  const mode = modeState.mode ?? "with_review";
  const [text, setText] = useState("");
  const [comments, setComments] = useState<Record<string, { text: string; open: boolean }>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // THE UNDO WINDOW LIVES IN `pendingSend` (Fable's bound): the page reads it, the app-level toast carries it to the next page.
  const pending = useSyncExternalStore(subscribePending, currentPending, currentPending);
  const done = useSyncExternalStore(subscribePending, lastDone, lastDone);
  const pendingHere = pending !== null && pending.key === sessionId;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!pendingHere) return;
    const t = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(t);
  }, [pendingHere]);
  const [outcome, setOutcome] = useState<string | null>(null);
  /* N2d — `Repeat next week`: ONE clone of this week's copy seven days on, through the one assign RPC; the client is told
     (one alert per act), and the reads re-run so the Next-week column appears. A refusal is said, never swallowed. */
  const [repeating, setRepeating] = useState(false);
  const [repeatSaid, setRepeatSaid] = useState<string | null>(null);
  async function repeatNextWeek(workoutId: string, day: string) {
    if (repeating || row === null) return;
    setRepeating(true); setRepeatSaid(null);
    const target = repeatDay(day);
    const r = await supabase.rpc("assign_coach_workout", { p_workout_id: workoutId, p_client_id: row.clientId, p_scheduled_date: target, p_note: "" });
    setRepeating(false);
    const o = describeOutcome(target, r.error);
    if (o.kind === "assigned") { tellTheClient(r.data ?? null, 1); write.reload(); return; }
    setRepeatSaid(o.kind === "alreadyAssigned" ? `Already on ${shortDate(target)}.` : o.message);
  }
  const r = useReviewRecorder();
  const rRef = useRef<ReviewRecorder>(r);
  rRef.current = r;
  // The object each container is being uploaded as, once Send runs (`<slot>|<kind>` → path) — the containers read the
  // queue's store by it (`Uploading…` → `▶ 0:41`).
  const [sending, setSending] = useState<Record<string, string>>({});
  const sendingPath = (slot: SlotKey) => (kind: MediaKind): string | null => sending[`${slot}|${kind}`] ?? null;
  // (No reset on `sessionId`: the route KEYS this page by the workout, so another workout is another instance — and leaving
  // this one inside the undo window SENDS it, by the unmount below.)

  const sent = w?.review ?? null;
  const seen = w === null || sent === null ? null : seenWords(reviewState(sessionId, w.comments.concat(w.review === null ? [] : [w.review])));
  const cells = useMemo(() => (w?.thisTree != null && w.nextTree != null ? nextWeekCells(w.thisTree, w.nextTree, kmKeys) : null), [w, kmKeys]);
  const hasNext = cells !== null;
  const column = sent === null && showsNextWeek(mode, hasNext);
  const allCells = useMemo(() => (cells === null ? [] : [...cells.values()].flatMap((c) => c ?? [])), [cells]);
  const plan = useMemo(() => cellWrites(allCells, column ? drafts : {}), [allCells, drafts, column]);
  const exercises = useMemo(() => (sheet === null ? [] : sheet.bands.flatMap((b) => b.kind === "section" ? b.items.flatMap((it) => it.kind === "single" ? [it.exercise] : [...it.exercises]) : [])), [sheet]);
  const words = useMemo(() => {
    if (!column || w?.thisTree == null || w.nextTree == null) return null;
    const names: Record<string, string> = {};
    for (const s of w.thisTree.coach_workout_sections ?? []) for (const e of s.coach_workout_exercises ?? []) if (e.exercise_id !== null && e.coach_exercises?.name) names[e.exercise_id] = e.coach_exercises.name;
    const short = new Map<string, string[]>();
    for (const ex of exercises) {
      const labels = ex.sets.filter((s) => s.state === "short").map((s) => s.label);
      if (ex.libraryId !== null && labels.length > 0) short.set(ex.libraryId, [...(short.get(ex.libraryId) ?? []), ...labels]);
    }
    return nextWeekWords(w.thisTree, draftedTree(w.nextTree, plan.writes), names, short, kmKeys);
  }, [column, w, plan, exercises, kmKeys]);
  const commentDrafts: CommentDraft[] = exercises.flatMap((ex) => {
    if (ex.commentTarget === null) return [];
    const c = comments[ex.commentTarget.exerciseRowId];
    const media = hasTake(r.rec.slots[ex.commentTarget.exerciseRowId]);
    return c === undefined && !media ? [] : [{ exerciseRowId: ex.commentTarget.exerciseRowId, coachExerciseId: ex.commentTarget.coachExerciseId, exercise: ex.name, text: c?.text ?? "", hasMedia: media }];
  });
  // A recording in flight is stopped by its own button first: its take lands a moment later, and Send reads the takes NOW.
  const blocker = r.rec.active !== null ? "Stop the recording to send." : sendBlocker(plan.invalid.length);
  const toSend = commentsToSend(commentDrafts).length;

  // `?exercise=<row>` — the Dashboard's REPLIES WAITING row lands on that exercise's thread (§8(6)).
  const landOn = routeQuery(window.location.hash).exercise ?? null;
  useEffect(() => {
    if (landOn === null || sheet === null || write.state.status !== "ready") return;
    document.getElementById(`ex-${landOn}`)?.scrollIntoView({ block: "center" });
  }, [landOn, sheet, write.state.status]);

  // THE SEND, CAPTURED AT CLICK TIME: the rows, the next-week writes and the column are read NOW, so the send that fires ten
  // seconds later (maybe after the coach has left this page) sends exactly what he saw. It sets no state of this page — the
  // store announces `lastDone`, and whichever page is showing this workout then reads it.
  const buildSend = useCallback((): (() => Promise<string | null>) | null => {
    if (sheet === null || row === null || w === null) return null;
    const rows = messageRows({
      coachId, clientId: row.clientId, sessionId: row.sessionId,
      title: reviewTitle(w.day, sheet.head.workout), text, comments: commentDrafts,
      media: { slots: r.rec.slots, newId: () => crypto.randomUUID() },
    });
    // The queue and its Blobs are captured NOW, like the rows: the commit may run after this page has gone.
    const items = uploadItems(coachId, rows.map((x) => ({ slot: slotOfRow(x), id: x.id ?? "" })), r.rec.slots);
    const blobs = new Map(items.map((i) => [i.ref, r.blobOf(i.ref)]));
    setSending(Object.fromEntries(items.map((i) => [`${i.slot}|${i.kind}`, i.path])));
    const writeWeek = column && plan.writes.length > 0;
    return async () => {
    const up = await runUploads(items, (ref) => blobs.get(ref), reviewUpload, reviewRemove);
    if (up.error !== null) return `Not sent: a recording did not upload (${up.error}). Nothing was sent — send again.`;
    const ins = await supabase.from("coach_messages").insert(rows).select("id, kind");
    if (ins.error !== null) { await undoUploads(up.landed, reviewRemove); return `Not sent: ${ins.error.message}`; }
    forgetUploads(up.landed);
    const reviewId = (ins.data ?? []).find((r) => r.kind === "review")?.id;
    // ONE push, for the review: `send-message-push` UNCHANGED (it alerts with the row's own body). A failed push leaves the
    // review sent — the thread is the record — and is said, never swallowed.
    let pushNote = "";
    if (reviewId !== undefined) {
      const pushed = await supabase.functions.invoke("send-message-push", { body: { messageId: reviewId } });
      if (pushed.error) pushNote = " Their phone was not notified.";
    }
    let weekNote = "";
    if (writeWeek) {
      const r = await applySetFieldWrites(updateSets, plan.writes);
      weekNote = r.error === null ? "" : ` Next week was not written (${String(r.landed)} of ${String(plan.writes.length)} changes landed): ${r.error}`;
    }
    announceReviewSent();   // F6: the queue, the position line and the sidebar count read again now
    return weekNote + pushNote === "" ? null : (weekNote + pushNote).trim();
    };
  }, [sheet, row, w, coachId, text, commentDrafts, column, plan, r]);

  // When THIS workout's send lands (here, or after the coach came back), say so and read the rows again.
  const seenDone = useRef<number>(done?.seq ?? 0);
  useEffect(() => {
    if (done === null || done.seq === seenDone.current) return;
    seenDone.current = done.seq;
    if (done.key !== sessionId) return;
    setOutcome(done.outcome);
    setDrafts({});
    setSending({});
    // A send that failed keeps the takes, so the coach can send again; a sent one hands them to the thread.
    if (!(done.outcome ?? "").startsWith("Not sent")) rRef.current.clear(Object.keys(rRef.current.rec.slots));
    write.reload();
  }, [done, sessionId, write]);

  const locked = sent !== null || pendingHere;
  function cardProps(ex: SheetExercise): CardWrite {
    const target = ex.commentTarget;
    const draft = target === null ? undefined : comments[target.exerciseRowId];
    const sentComment = target === null ? null : (w?.comments.find((c) => c.exerciseRowId === target.exerciseRowId) ?? null);
    const thread = sentComment === null || w === null ? [] : threadOf(sentComment.id, w.thread);
    const byId = new Map((w?.thread ?? []).map((m) => [m.id, m]));
    return {
      next: column && ex.planRowId !== null ? (cells?.get(ex.planRowId) ?? null) : undefined,
      drafts, invalid: plan.invalid, locked,
      onCell: (id, v) => setDrafts((d) => ({ ...d, [id]: v })),
      comment: target === null ? null : {
        sent: sentComment === null ? null : splitTitled(sentComment.body).text,
        sentTakes: sentComment === null ? null : takesOf(sentComment),
        slot: target.exerciseRowId, r, coachId, sendingPath: sendingPath(target.exerciseRowId),
        replySlot: sentComment === null ? null : replySlot(sentComment.id),
        thread: thread.map((m) => ({ id: m.id, mine: m.senderId === coachId, text: m.body, takes: takesOf(m), voiceHome: mediaHome(m, "voice", byId) })),
        reply: sentComment === null || row === null ? null : async (body: string) => {
          // A reply is a plain message quoting the thread's LATEST row (root: the comment) — the same-thread trigger checks it.
          // Its takes (voice, video) upload FIRST into the coach's own folder; the row names them; a failed insert removes them.
          const quote = thread[thread.length - 1]?.id ?? sentComment.id;
          const slot = replySlot(sentComment.id);
          const takes = r.rec.slots[slot];
          const id = crypto.randomUUID();
          const items = uploadItems(coachId, [{ slot, id }], r.rec.slots);
          const up = await runUploads(items, (ref) => r.blobOf(ref), reviewUpload, reviewRemove);
          if (up.error !== null) return `a recording did not upload (${up.error})`;
          const ins = await supabase.from("coach_messages")
            .insert({ id, coach_id: coachId, client_id: row.clientId, sender_id: coachId, body: replyBody(body, takes), reply_to: quote, ...mediaFields(coachId, id, takes) })
            .select("id").single();
          if (ins.error !== null) { await undoUploads(up.landed, reviewRemove); return ins.error.message; }
          forgetUploads(up.landed);
          r.clear([slot]);
          void supabase.functions.invoke("send-message-push", { body: { messageId: ins.data.id } });
          write.reload();
          announceReviewSent();   // a thread answered leaves REPLIES WAITING
          return null;
        },
        text: draft?.text ?? "",
        open: draft?.open ?? false,
        set: (patch) => setComments((c) => ({ ...c, [target.exerciseRowId]: { text: c[target.exerciseRowId]?.text ?? "", open: c[target.exerciseRowId]?.open ?? false, ...patch } })),
      },
    };
  }

  return (
    <>
    <Workspace>
      <main id="main" className="review-page ours-page">
        <p className="review-crumb"><a className="review-crumb-link" href={href({ name: "dashboard" })}>{positionLine(position)}</a></p>
        <header className="review-head">
          {sheet !== null && <Avatar name={sheet.head.client} className="review-avatar"
                                     photoUrl={(() => { const link = data?.rows.find((x) => x.clientId === row?.clientId)?.linkId;
                                       return link === undefined ? null : (data?.profile[link]?.photoUrl ?? null); })()} />}
          <div className="review-head-text">
            <h1 className="review-title" ref={heading} tabIndex={-1}>
              {sheet === null ? "Workout review" : sheet.head.client}
            </h1>
            {sheet !== null && (
              <p className="review-meta">
                <span>{sheet.head.line}</span>
                {sheet.head.chips.map((c) => (
                  <span key={c.text} className={`queue-chip is-${c.tone}`}>{c.text}</span>
                ))}
              </p>
            )}
          </div>
          {/* 🔴 `Edit next week` (his idea, 2026-10-07): ONE door to the whole of next week — order, supersets, sets,
              exercises — in the builder, over this page. Where the Next-week column is, and never inside the undo window. */}
          {column && !pendingHere && w?.nextWorkoutId != null && (
            <a className="ht-button review-edit-next" href={href({ name: "reviewNextWeek", sessionId, workoutId: w.nextWorkoutId })}>
              <Icon name="pencil" size={15} />{EDIT_NEXT_WEEK_LABEL}
            </a>
          )}
          <MuteSwitch />
          {position !== null && (
            <a className="ht-button ht-button-quiet review-skip" href={skipHref}
               aria-label={skip === null ? "Skip — back to the review queue" : "Skip to the next workout in the queue"}>Skip</a>
          )}
        </header>

        {loading && (
          <p className="ht-callout ours-quiet" aria-live="polite">Loading the workout…</p>
        )}
        {failed !== null && (
          <div role="alert" className="ours-error">
            <p className="ht-body">Could not load this workout.</p>
            <p className="ht-caption">{failed}</p>
          </div>
        )}
        {outside && (
          <p className="ht-callout ours-quiet">
            This workout is not in your review window — it finished before last Monday, or nothing was logged in it.{" "}
            <a className="ht-link" href={href({ name: "dashboard" })}>Back to the queue</a>
          </p>
        )}

        {(r.notice ?? r.voice.error ?? r.video.error) !== null && (
          <p className="review-quiet-line review-media-notice" role="status">{r.notice ?? r.voice.error ?? r.video.error}</p>
        )}
        <ReviewRecordingBubble r={r} />
        {sheet !== null && (
          <div className="review-cols">
            <section className="review-card" aria-labelledby="review-did">
              <header className="review-card-head">
                <h2 id="review-did" className="review-card-title">What they did</h2>
                {/* JOB 3 · WILLIAM (10-15): the caption `written · done · next week is editable` named the table's own column heads
                    (Written · Done · Next week) — gone; only the RPE fact, which no column says, stays. */}
                {COACH_SEES_RPE && !sheet.rpeLogged && <span className="review-card-sub">no RPE logged</span>}
              </header>
              {sheet.otherRecordsLine !== null && <p className="review-quiet">{sheet.otherRecordsLine}</p>}
              {sheet.unpaired !== null && <p className="review-quiet">{sheet.unpaired}</p>}
              {sheet.workoutNote !== null && (
                <p className="review-workout-note"><span className="review-note-label">Their note</span>“{sheet.workoutNote}”</p>
              )}
              {sheet.bands.map((b, bi) => b.kind === "folded"
                ? <p key={bi} className="review-folded">{b.line}</p>
                : (
                  <div key={bi} className="review-band">
                    {b.title !== null && <h3 className="review-band-title">{b.title}</h3>}
                    {b.items.map((it, ii) => it.kind === "single"
                      ? <ExerciseCard key={ii} ex={it.exercise} rpe={COACH_SEES_RPE && sheet.rpeLogged} demo={demoOf(media, it.exercise)} {...cardProps(it.exercise)} />
                      : (
                        <div key={ii} className="review-superset" aria-label="Superset">
                          <p className="review-superset-label">Superset</p>
                          {it.exercises.map((ex) => <ExerciseCard key={ex.id} ex={ex} rpe={COACH_SEES_RPE && sheet.rpeLogged} demo={demoOf(media, ex)} {...cardProps(ex)} />)}
                        </div>
                      ))}
                  </div>
                ))}
            </section>
            <div className="review-side">
              <section className="review-card" aria-labelledby="review-yours">
                <header className="review-card-head">
                  <h2 id="review-yours" className="review-card-title">Your review</h2>
                  {(sent === null ? toSend : (w?.comments.length ?? 0)) > 0 && (
                    <span className="review-card-sub">
                      {String(sent === null ? toSend : (w?.comments.length ?? 0))} exercise {(sent === null ? toSend : (w?.comments.length ?? 0)) === 1 ? "comment" : "comments"}
                    </span>
                  )}
                </header>
                {sent !== null ? (
                  <div className="review-compose">
                    {splitTitled(sent.body).text !== "" && <p className="review-sent-text">{splitTitled(sent.body).text}</p>}
                    {hasSentTakes(takesOf(sent)) && (
                      <div className="rm-row is-sent"><SentMedia row={takesOf(sent)} home="review" viewerId={coachId} senderIsCoach /></div>
                    )}
                    <p className="review-sent-line" role="status">{SENT_LINE}</p>
                    {seen !== null && <p className="review-quiet-line review-seen">{seen === "seen" ? "Seen" : "Not seen yet"}</p>}
                    <a className="ht-link review-thread-link" href={href({ name: "thread", clientId: row?.clientId ?? "" })}>Open the thread ›</a>
                  </div>
                ) : (
                  <div className="review-compose">
                    {/* Job 3 step 5 page 2 (2026-10-09): the overall is HIS COMPOSER (DECIDED 10-07), the phone's Part D shape —
                        `+` (Video · Live review · Photos) · the field `Overall` growing to ~8 lines · the mic inside. */}
                    <ReviewComposer slot={OVERALL} r={r} locked={pendingHere} sendingPath={sendingPath(OVERALL)} name="the whole workout"
                                    id="review-text" label="Your review of the whole workout" placeholder="Overall"
                                    text={text} onText={setText} maxHeight={COMPOSER_OVERALL_MAX} live />
                    {r.video.mime === null && <p className="review-quiet-line">Video: {VIDEO_UNSUPPORTED}</p>}
                    {!pendingHere ? (
                      <button type="button" className="ht-button ht-button-primary review-send"
                              disabled={w === null || blocker !== null}
                              onClick={() => {
                                const send = buildSend();
                                if (send === null) return;
                                setOutcome(null);
                                startPending({ key: sessionId, label: `${sheet.head.client.split(" ")[0] ?? "the"}'s review`, ms: UNDO_SECONDS * 1000, commit: send });
                              }}>
                        {primaryLabel(mode, hasNext)}
                      </button>
                    ) : (
                      <p className="review-pending" role="status">
                        <span>{pendingWords(pending, now)}</span>
                        <button type="button" className="ht-button ht-button-quiet review-undo" onClick={() => undoPending(sessionId)}>Undo</button>
                      </p>
                    )}
                    {blocker !== null && <p className="review-quiet-line" role="alert">{blocker}</p>}
                    <p className="review-quiet-line">Final once sent — add to it in the thread.</p>
                  </div>
                )}
                {outcome !== null && <p className="review-quiet-line review-outcome" role="alert">{outcome}</p>}
                {write.state.status === "error" && <p className="review-quiet-line review-outcome" role="alert">{write.state.message}</p>}
              </section>

              <section className="review-card" aria-labelledby="review-next">
                <header className="review-card-head">
                  <h2 id="review-next" className="review-card-title">Next week</h2>
                  {words !== null && words.changes > 0 && <span className="queue-chip is-plain">{words.changes} {words.changes === 1 ? "change" : "changes"}</span>}
                </header>
                {words !== null ? (
                  <ul className="review-next-list" aria-label="What next week changes">
                    {words.moved.map((m) => (
                      <li key={m.key} className="review-next-item">
                        <span className="review-next-name">{m.name}</span>
                        <span className="review-next-move"><MoveParts moves={m.moves} />{rowChangeParts(m.addedRows, m.droppedRows).map((s, i) => <span key={i}>{(m.moves.length > 0 || i > 0) && " · "}{s}</span>)}</span>
                      </li>
                    ))}
                    {words.frozen.map((f) => (
                      <li key={f.key} className="review-next-item">
                        <span className="review-next-name">{f.name}</span>
                        <span className="review-next-move"><span className="proposal-move">{f.from} <span className="proposal-to">→ {f.to}</span></span></span>
                      </li>
                    ))}
                    {words.held.map((h) => (
                      <li key={h.name} className="review-next-item"><span className="review-next-name">{h.name}</span><span className="review-next-move">{h.text}</span></li>
                    ))}
                    {words.unchanged > 0 && <li className="review-next-item is-quiet">+ {words.unchanged} unchanged</li>}
                  </ul>
                ) : (
                  <p className="review-quiet-line review-next-absent">
                    {sent !== null && hasNext ? "Set on their Week." : (w === null ? "Reading next week…" : (nextWeekAbsent(mode) ?? ""))}
                  </p>
                )}
                {w !== null && w.thisWorkoutId !== null && w.day !== null && canRepeat(mode, hasNext, w.thisWorkoutId, w.day) && (() => {
                  const [id, day] = [w.thisWorkoutId, w.day];
                  return (
                    <div className="review-repeat">
                      <button type="button" className="ht-button" disabled={repeating} onClick={() => { void repeatNextWeek(id, day); }}>
                        {repeating ? "Repeating…" : REPEAT_LABEL}
                      </button>
                      {repeatSaid !== null && <p className="ht-caption" role="alert">{repeatSaid}</p>}
                    </div>
                  );
                })()}
                <p className="review-quiet-line"><a className="review-mode-link" href={`${href({ name: "page" })}?show=review-mode`}>{MODE_LINK}</a></p>
              </section>
            </div>
          </div>
        )}
      </main>
    </Workspace>
    {/* The builder, BESIDE the page (a modal `<dialog>`, so the page is inert behind it): next week's COPY, opening with the
        drafted cells on their sets (`carryIntoWorkout`); its Save is its own — the drafts it carried are spent (their sets
        were replaced) and the page reads again. A discard writes nothing; the drafts stay. */}
    {nextWeekWorkoutId !== null && (
      <WorkoutBuilder workoutId={nextWeekWorkoutId} route={{ name: "reviewNextWeek", sessionId, workoutId: nextWeekWorkoutId }}
                      carry={nextWeekWorkoutId === w?.nextWorkoutId ? plan.writes : []}
                      onSaved={() => { setDrafts({}); write.reload(); }} />
    )}
    </>
  );
}


type CardWrite = {
  /** Next week's cells for this exercise: `undefined` = no column on this page; `null` = not in next week. */
  next: NextCell[] | null | undefined;
  drafts: Readonly<Record<string, string>>;
  invalid: readonly string[];
  locked: boolean;
  onCell: (setId: string, value: string) => void;
  comment: {
    sent: string | null; text: string; open: boolean; set: (p: { text?: string; open?: boolean }) => void;
    /** Step 5: the sent comment's takes; the draft's slot on the ONE recorder; where Send is uploading it as. */
    sentTakes: SentTakes | null; slot: SlotKey; r: ReviewRecorder; coachId: string; sendingPath: (kind: MediaKind) => string | null;
    replySlot: SlotKey | null;
    /** The exercise's thread under the sent comment (§8: replies without end), and the coach's reply to it. */
    thread: { id: string; mine: boolean; text: string; takes: SentTakes; voiceHome: "review" | "inbox" }[];
    reply: ((body: string) => Promise<string | null>) | null;
  } | null;
};

/** `rpe` — whether the workout logged ANY RPE: without one the column is not drawn (the card's head says so once). */
/** The exercise's library demo for its card's square; none (the dumbbell) where the library row has no video. */
function demoOf(media: ReadonlyMap<string, ExerciseMedia>, ex: SheetExercise): ExerciseMedia {
  return (ex.libraryId === null ? undefined : media.get(ex.libraryId)) ?? { demoVideoUrl: null, thumbnailUrl: null };
}

function ExerciseCard({ ex, rpe, demo, next, drafts, invalid, locked, onCell, comment }: { ex: SheetExercise; rpe: boolean; demo: ExerciseMedia } & CardWrite) {
  const col = next !== undefined;
  const clips = clipsByRow(ex.clips);
  const fit = useOneLineTable();
  const rowCells = cellsForRows(ex.sets, next);
  return (
    <article className="review-ex" id={ex.commentTarget === null ? undefined : `ex-${ex.commentTarget.exerciseRowId}`}>
      {/* JOB 3 · WILLIAM (10-15) — HIS WORD: *"use video containers but not these huge ones … small ones that can fit before each
          exercise title … on their left, around above the set text. so the coach can check which exercise that is if he doesn't
          remember only by title"*. The demo is a small tile left of the name (the builder card's size), on every card. */}
      <div className="review-ex-title">
        <VideoThumb demoVideoUrl={demo.demoVideoUrl} thumbnailUrl={demo.thumbnailUrl} size="mini" />
        <h4 className="review-ex-name">{ex.name}</h4>
      </div>
      {/* The rule sentence is engine data, withheld from coaches for now (`COACH_SEES_ENGINE`, his 2026-09-28 word). */}
      {COACH_SEES_ENGINE && (
        <p className="review-rule">
          {ex.rule === null ? "No rule" : ex.rule.text}
          {ex.rule?.lacks != null && <span className="review-rule-lacks"> — {ex.rule.lacks}</span>}
        </p>
      )}
      {/* 🔴 HIS LAYOUT (2026-10-07 ~12:20, again ~13:40, QUOTED): *"under the video titles we need to have the square video
          containers of each exercise … and under that … the video and voice buttons … horizontally not one on top of the
          other … the rest … moving to the right. so the next week will end up on the right side of the exercise container"*.
          The left column: the exercise's own demo (the library row's, the builder's `VideoThumb`), then Voice · Video in
          one row; the right: the set table, as wide as the card leaves it (REVISES W93's "pushed left, no stretch"). */}
      {/* The left column now holds only the SENT comment's takes (job 3 step 5 page 2: a DRAFT's media live in the composer
          under the card); the demo moved beside the name (his 10-15 word above), so the table takes the card's width. */}
      <div className="review-ex-body">
        {comment !== null && comment.sent !== null && comment.sentTakes !== null && hasSentTakes(comment.sentTakes) && (
        <div className="review-ex-side">
          <div className="review-ex-media">
            <SentMedia row={comment.sentTakes} home="review" viewerId={comment.coachId} senderIsCoach stage />
          </div>
        </div>
        )}
        <div className="review-sets-fit" ref={fit.wrap}>
        <table className="review-sets" ref={fit.table}>
          <thead>
            <tr><th scope="col">Set</th><th scope="col">Written</th><th scope="col">Done</th>{rpe && <th scope="col" className="review-rpe-h">RPE</th>}{col && <th scope="col" className="review-next-h">Next week</th>}</tr>
          </thead>
          <tbody>
            {ex.sets.map((s, i) => {
              const cell = rowCells[i];
              return <SetRows key={s.label} s={s} rpe={rpe} col={col} cell={cell} first={s === ex.sets.find((x) => x.state !== "ramp")}
                              dropped={next === null} name={ex.name} drafts={drafts} invalid={invalid} locked={locked} onCell={onCell}
                              clips={clips.byRow.get(s.label) ?? []} />;
            })}
          </tbody>
        </table>
        </div>
      </div>
      {/* W116: a clip naming no set row plays from the ONE button under the table — never a second player. */}
      {clips.loose.length > 0 && <div className="review-clips"><ClipPlayButton clips={clips.loose} setWords={ex.name} setLabel={null} /></div>}
      {comment !== null && <CommentLine c={comment} locked={locked} name={ex.name} />}
    </article>
  );
}

/** A sent row's takes, for `SentMedia`. */
function takesOf(m: { id: string; senderId: string; audioPath: string | null; audioSeconds: number | null; videoPath: string | null; videoSeconds: number | null; photoPath: string | null }): SentTakes {
  return { id: m.id, senderId: m.senderId, audioPath: m.audioPath, audioSeconds: m.audioSeconds, videoPath: m.videoPath, videoSeconds: m.videoSeconds, photoPath: m.photoPath };
}
const hasSentTakes = (t: SentTakes | null) => t !== null && (t.audioPath !== null || t.videoPath !== null || t.photoPath !== null);

/** ONE comment per exercise (his bound) as HIS COMPOSER (job 3 step 5 page 2, DECIDED 10-07): `+` (Video · Photos) · the
 *  field `Comment` that grows · the mic inside — one of each take, on the page's one recorder. Sending (the undo window)
 *  freezes it where it stands. Once sent, the words and the takes stand, the takes keep their × (the owner removes the
 *  object), and the thread goes on below with its own composer behind `Reply`. */
function CommentLine({ c, locked, name }: { c: NonNullable<CardWrite["comment"]>; locked: boolean; name: string }) {
  if (c.sent !== null) {
    return (
      <div className="review-thread">
        {c.sent !== "" && <p className="review-comment is-sent"><span className="review-comment-label">Your comment</span>{c.sent}</p>}
        {c.thread.map((m) => (
          <div key={m.id} className={m.mine ? "review-reply is-mine" : "review-reply"}>
            <p className="review-reply-text"><span className="review-comment-label">{m.mine ? "You" : "They replied"}</span>{m.text}</p>
            {hasSentTakes(m.takes) && (
              <div className="rm-row is-sent"><SentMedia row={m.takes} home={m.voiceHome} viewerId={c.coachId} senderIsCoach={m.mine} /></div>
            )}
          </div>
        ))}
        {c.reply !== null && c.replySlot !== null && <ReplyBox name={name} send={c.reply} slot={c.replySlot} r={c.r} />}
      </div>
    );
  }
  return (
    <ReviewComposer slot={c.slot} r={c.r} locked={locked} sendingPath={c.sendingPath} name={name}
                    id={`comment-${c.slot}`} label={`Comment on ${name} — they see it on this exercise next time`} placeholder="Comment"
                    text={c.text} onText={(t) => c.set({ text: t })} maxHeight={COMPOSER_MAX} />
  );
}


/** A reply under an exercise's thread (§8: without end) — words, and/or a voice note, a round video and a photo on the page's
 *  one recorder (step 5); the takes upload into the coach's own folder when Reply is sent. Part E (Fable 2026-10-08): behind
 *  the `Reply` doorway the SAME parts as the overall — since job 3 step 5 page 2 the composer (`Reply`, + Video · Photos,
 *  the mic inside; no Live review: it belongs to the review, not a reply). Its Send is the THREAD's (job 3 step 6 page 2,
 *  Fable's ruling: ONE place for Send) — a round ↑ inside the field where the mic stood, the Inbox's `<form>` shape. */
function ReplyBox({ name, send, slot, r }: { name: string; send: (body: string) => Promise<string | null>; slot: SlotKey; r: ReviewRecorder }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const takes = hasTake(r.rec.slots[slot]);
  const recordingHere = r.rec.active !== null && r.rec.active.slot === slot;
  if (!open && !takes) return <p className="review-comment-line"><button type="button" className="review-comment-add" onClick={() => setOpen(true)}>Reply</button></p>;
  return (
    <form className="review-comment is-open review-reply-box" onSubmit={(e) => {
      e.preventDefault();
      if (busy || recordingHere || (body.trim() === "" && !takes)) return;
      setBusy(true);
      void send(body).then((err) => { setBusy(false); setError(err); if (err === null) { setBody(""); setOpen(false); } });
    }}>
      <ReviewComposer slot={slot} r={r} locked={busy} sendingPath={() => null} name={`your reply about ${name}`}
                      id={`reply-${slot}`} label={`Reply about ${name}`} placeholder="Reply"
                      text={body} onText={setBody} maxHeight={COMPOSER_MAX} autoFocus send={{ busy }} />
      {error !== null && <p className="review-quiet-line" role="alert">Not sent: {error}</p>}
    </form>
  );
}

function SetRows({ s, rpe, col, cell, first, dropped, name, drafts, invalid, locked, onCell, clips }: {
  s: SheetSetRow; rpe: boolean; col: boolean; cell: NextCell | undefined; first: boolean; dropped: boolean; name: string;
  clips: SheetExercise["clips"];
} & Pick<CardWrite, "drafts" | "invalid" | "locked" | "onCell">) {
  const value = cell === undefined ? "" : (drafts[cell.setId] ?? cellTextFor(cell));
  const moved = cell !== undefined && (cell.proposed || (drafts[cell.setId] !== undefined && drafts[cell.setId] !== cellTextFor(cell)));
  return (
    <>
      <tr className={s.state === "ramp" ? "review-set is-ramp" : "review-set"}>
        <td className="review-set-n">{s.label}</td>
        <td className="review-written">{s.written}</td>
        <td className={DONE_CLASS[s.state]}>
          <span className="review-done-line">
            <span>{doneWords(s)}</span>
            <ClipPlayButton clips={clips} setWords={`${name}, set ${s.label}`} setLabel={s.label} />
            {s.note !== null && <SetNote note={s.note} setWords={`${name}, set ${s.label}`} />}
          </span>
        </td>
        {rpe && <td className="review-rpe">{s.rpe}</td>}
        {col && (
          <td className="review-next">
            {cell !== undefined && cell.frozen ? <span className="review-next-same">{frozenWords(cell.frozenWords, s)}</span> : cell !== undefined ? (
              <span className={`review-next-cell${moved ? " is-proposed" : ""}${invalid.includes(cell.setId) ? " is-invalid" : ""}`}>
                <input className="review-next-input" inputMode="decimal"
                       aria-label={`${name}, ${cell.warmUp ? `warm-up ${s.label.slice(1)}` : `set ${s.label}`}, next week ${cellUnitFor(cell)}`}
                       value={value} disabled={locked} onChange={(e) => onCell(cell.setId, e.target.value)} />
                <span className="review-next-unit">{cellUnitFor(cell)}</span>
              </span>
            ) : dropped && first ? <span className="review-next-none">not in next week</span> : null}
          </td>
        )}
      </tr>
    </>
  );
}

/** A row's Done words: a run's names only what moved against its Written (`movedParts`) — `in 1:46:40`, `380 m`, `same`;
 *  a lift's, a ramp's, `–` and `skipped` as the sheet wrote them (`CoachWorkoutReviewView.doneWords`' twin). */
function doneWords(s: SheetSetRow): string {
  return s.typed && (s.state === "equal" || s.state === "short" || s.state === "beaten") ? movedParts(s.done, s.written) : s.done;
}

/** A frozen Next-week cell's words: `same`, else only what moves against this week's Written (`10 km`, `@ 36 kg`). */
function frozenWords(words: string | null, s: SheetSetRow): string {
  const w = words ?? "";
  return w === MOVED_NONE ? w : movedParts(w, s.written);
}

/** 🔴 ONE LINE PER ROW, ONE SIZE PER TABLE (his word 2026-10-07: "no row should be 2 lines, none. always 1 to be tidy").
 *  RENDERED before: `5 km in` / `25:00` in Safari at 402 pt, `3 @ 12.5` / `kg` at 390. Every cell is `nowrap`; when the
 *  table is wider than its room it first drops its column minimums (`is-tight`, every column as wide as its words), then
 *  takes ONE scale (`--review-fit`) — the room over its width, measured again at that size (the type's tracking moves), never
 *  below `REVIEW_FIT_FLOOR` (the phone's `ReviewPhoneWords.rowFloor`). Re-fitted whenever the card's body changes width. */
export const REVIEW_FIT_FLOOR = 0.7;

/** `needs` shared out of `room`: the smallest first, each keeping its need while it fits an equal share of what is left,
 *  the rest an equal share each (`ReviewPhoneWords.waterFill`'s twin). */
export function waterFill(needs: readonly number[], room: number): number[] {
  const out = [...needs];
  let left = room, k = needs.length;
  for (const i of needs.map((_, j) => j).sort((a, b) => (needs[a] ?? 0) - (needs[b] ?? 0))) {
    out[i] = Math.min(needs[i] ?? 0, left / k);
    left -= out[i] ?? 0;
    k -= 1;
  }
  return out;
}
function useOneLineTable() {
  const wrap = useRef<HTMLDivElement>(null);
  const table = useRef<HTMLTableElement>(null);
  useLayoutEffect(() => {
    const w = wrap.current, t = table.current;
    if (w === null || t === null) return;
    const fit = () => {
      t.classList.remove("is-tight", "is-floor");
      t.style.removeProperty("--review-fit");
      const head = Array.from(t.tHead?.rows[0]?.cells ?? []);
      for (const th of head) th.style.removeProperty("width");
      const room = w.clientWidth;
      if (t.offsetWidth <= room) return;
      t.classList.add("is-tight");
      let scale = 1;
      for (let i = 0; i < 3 && t.offsetWidth > room && scale > REVIEW_FIT_FLOOR; i += 1) {
        scale = Math.max(REVIEW_FIT_FLOOR, scale * room / t.offsetWidth);
        t.style.setProperty("--review-fit", scale.toFixed(3));
      }
      if (t.offsetWidth <= room) return;
      // At the floor and still too wide: the columns whose words fit a fair share keep them whole, the widest share the rest
      // and a word too long ends in `…` (the phone's `.lineLimit(1)`) — never a column cut at the card's edge (RENDERED: `6 kr`).
      // 2 px over each column's measured width: WebKit's fixed layout gives a collapsed border's pixel back to the content
      // (RENDERED in Safari: a column exactly as wide as its words read `6 km in 30:…`).
      const widths = waterFill(head.map((th) => th.offsetWidth + 2), room);
      head.forEach((th, i) => { th.style.width = `${String(widths[i] ?? 0)}px`; });
      t.classList.add("is-floor");
    };
    fit();
    // A web font that lands after the first fit changes every width (RENDERED in Safari: `6 km in 30:…` beside a half-empty
    // Done — the columns were shared out in the fallback font's widths), so the table is fitted again when one loads.
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    fonts?.addEventListener("loadingdone", fit);
    void fonts?.ready.then(fit);
    const body = w.parentElement;
    const ro = body === null || typeof ResizeObserver === "undefined" ? null : new ResizeObserver(fit);
    if (body !== null) ro?.observe(body);
    return () => { ro?.disconnect(); fonts?.removeEventListener("loadingdone", fit); };
  });
  return { wrap, table };
}

/** W93 (his word, 2026-09-28): "some switch somewhere on top which will save its last state for all future reviews of all
 *  clients, of play all videos muted (the client's ones)". One per-viewer answer (`clipsMuted`), read by every clip stage. */
function MuteSwitch() {
  const muted = useSyncExternalStore(subscribeClipsMuted, clipsMuted, clipsMuted);
  return (
    <label className="review-mute">
      <input type="checkbox" role="switch" className="review-mute-input" checked={muted}
             onChange={(e) => setClipsMuted(e.target.checked)} />
      <span className="review-mute-track" aria-hidden="true" />
      <span>Client videos muted</span>
    </label>
  );
}
